import { Capacitor } from "@capacitor/core";
import { useLocation } from "wouter";
import { useCurrentUser } from "@client/hooks/use-current-user";
import { useEffectiveUser } from "@client/hooks/use-effective-user";
import {
  getDownloadEngine,
  mobileVideoStore,
} from "@client/iphone/lib/downloadEngine";
import {
  useImpersonationBanner,
  useRoundAdmin,
} from "@client/ImpersonateContext";
import { useImpersonationRefValue } from "@client/ImperativeState";
import { trpc, trpcVanilla } from "@client/trpc";
import { useVideoCatalog } from "@client/iphone/hooks/useVideoCatalog";
import {
  createVideoSync,
  type VideoSyncController,
} from "@client/iphone/lib/videoSync/createVideoSync";
import { MobileVideoStore } from "@client/iphone/lib/mobileVideoStore";
import {
  useVideoStore,
  VideoStoreProvider,
} from "@client/iphone/lib/VideoStoreContext";
import {
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";

// Web/iPad-web video-state provider for the funder workspace. Holds a fresh
// in-memory MobileVideoStore (no localStorage, no download engine). Mounted at
// the app shell so it wraps BOTH the navbar (SiteHeader -> QueueDrawer) and the
// round routes (funder pages + their bookmark hooks). The parent keys it on
// (roundId, viewAsUserId) so an impersonation/round switch remounts a clean
// instance. `roundId` is null outside a round: the store stays empty and no
// sync runs.
export function QueueProvider({
  roundId,
  children,
}: {
  roundId: string | null;
  children: ReactNode;
}) {
  const { isFunder } = useRoundAdmin();
  const { data: effectiveUser } = useEffectiveUser();
  const { data: currentUser } = useCurrentUser();
  const { isImpersonating } = useImpersonationBanner();

  const enabled =
    (isFunder || effectiveUser?.isSfcAdmin === true) && roundId != null;
  const canWrite = enabled && !isImpersonating;
  // Key the (module-level, in-memory) outbox by the real signed-in user so a
  // round/impersonation remount keeps pending writes but a different real user
  // never flushes them.
  const outboxKey = currentUser?.userId ?? "anonymous";

  // Reuse the persisted singleton store + download engine only for a real
  // (non-impersonated) native session, so downloads survive restarts and the
  // engine + drawer read one instance. While impersonating, fall back to the
  // ephemeral in-memory store (no persistence, no engine) like the web path —
  // an impersonated user's playlist/bookmarks/progress must never land in this
  // device's localStorage, and downloads must not run under the acting-as
  // identity.
  const useNativeDownloads = Capacitor.isNativePlatform() && !isImpersonating;

  // Web (and impersonated native): a fresh per-(round,user) in-memory store,
  // no engine, no persistence. Keep the useMemo unconditional so hook order is
  // stable across renders.
  const webStore = useMemo(() => new MobileVideoStore({ persist: false }), []);
  const store = useNativeDownloads ? mobileVideoStore : webStore;

  return (
    <VideoStoreProvider store={store} canWrite={canWrite}>
      {enabled && roundId != null && (
        <QueueLifecycle
          roundId={roundId}
          canWrite={canWrite}
          outboxKey={outboxKey}
          useNativeDownloads={useNativeDownloads}
        />
      )}
      {children}
    </VideoStoreProvider>
  );
}

// Loads the round catalog into the (context) store and drives sync + hydration.
// Rendered only when enabled, so non-funders issue no getVideos query and run
// no sync. Lives inside the provider so useVideoStore/useVideoCatalog resolve
// the same instance the children read.
function QueueLifecycle({
  roundId,
  canWrite,
  outboxKey,
  useNativeDownloads,
}: {
  roundId: string;
  canWrite: boolean;
  outboxKey: string;
  useNativeDownloads: boolean;
}) {
  const store = useVideoStore();
  const utils = trpc.useUtils();
  // Matches VideoStatesProvider's tRPC query input so hydration shares its
  // cache entry. Constant for this instance (QueueBoundary remounts on switch).
  const viewAsUserId = useImpersonationRefValue();
  const catalog = useVideoCatalog({
    roundId,
    persistedItems: useNativeDownloads ? store.itemsInOrder() : [],
  });
  const syncRef = useRef<VideoSyncController | null>(null);

  useEffect(() => {
    // Fresh sync controller for this lifetime, bound to the per-user in-memory
    // outbox (persistOutbox: false + outboxKey). The outbox survives remounts
    // for the same user (pending writes aren't dropped on a round/impersonation
    // switch) but is isolated per real user.
    //
    // The store is a brand-new in-memory instance on every (round, viewAsUserId)
    // remount, so it always hydrates from a fresh server query. As a side effect
    // we SEED VideoStatesProvider's React Query cache (same key as its hook:
    // `{ roundId, viewAsUserId }`) but only when it's empty — on the initial
    // load the player's hook then reads this entry instead of firing its own
    // duplicate getUserVideoState request. We never overwrite an existing entry:
    // the player writes optimistic, newer-timestamped progress rows there that a
    // just-fetched (older) server response must not clobber. viewAsUserId is
    // constant for this instance — QueueBoundary keys the whole provider on
    // (roundId, viewAsUserId), so a switch remounts this effect.
    const sync = createVideoSync(store, {
      persistOutbox: false,
      outboxKey,
      fetchUserVideoState: async () => {
        const server = await trpcVanilla.getUserVideoState.query({ roundId });
        utils.getUserVideoState.setData(
          { roundId, viewAsUserId: viewAsUserId ?? undefined },
          (prev) => prev ?? server,
        );
        return server;
      },
    });
    syncRef.current = sync;

    if (canWrite) {
      // Real funder session: install bridges (done by createVideoSync) and
      // start the worker, which flushes the outbox.
      sync.start();
    } else {
      // Impersonating: read-only. Clear the bridges so the store enqueues
      // nothing, and DON'T start the worker — starting it would flush the real
      // user's queued writes under the impersonated identity (the trpc client
      // sends the acting-as header). Hydration below is read-only.
      store.setSyncFeatures(null);
    }

    void sync.hydrateFromServer(roundId);

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Refresh the store so a returning tab picks up cross-device changes.
        void sync.hydrateFromServer(roundId);
      }
    };

    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      sync.stop();
      syncRef.current = null;
    };
  }, [roundId, store, canWrite, outboxKey, utils, viewAsUserId]);

  // Downloads are a property of the device, not of the viewed identity, so the
  // engine runs on any native session — including while impersonating, where
  // the context store above is the ephemeral per-view copy but downloads target
  // the device-global store the engine owns. (Sync WRITES are still suppressed
  // while impersonating via `canWrite` above; only downloads, which are
  // read-only server fetches to this device, run here.)
  const native = Capacitor.isNativePlatform();

  // Ensure the download engine is running. Idempotent — start() guards on an
  // internal `started` flag. The engine no-ops unless a round's download toggle
  // is on.
  useEffect(() => {
    if (!native) {
      return;
    }

    void getDownloadEngine().start();
  }, [native]);

  // Every navigation suspends in-flight download transfers for a bounded
  // window (see holdForNavigation): concurrent multi-GB video transfers
  // saturate the link and starve the navigation's own API fetches, which on
  // the iPad reads as a tap that registered but never changed the page.
  const [location] = useLocation();
  useEffect(() => {
    if (!native) {
      return;
    }

    getDownloadEngine().holdForNavigation();
  }, [native, location]);

  // Auto-enqueue this round's videos as the catalog discovers them (mirrors
  // IPhoneApp). syncWithCatalog only enqueues videos whose round has the
  // download toggle enabled. Keyed on BOTH the catalog and the round's enabled
  // flag (read from the device-global store the toggle writes to), so the
  // enqueue fires whichever happens last: toggling "Download all" before the
  // catalog loads, or the catalog landing after the round is already enabled.
  const roundDownloadEnabled = useSyncExternalStore(
    (listener) => mobileVideoStore.subscribe(listener),
    () => mobileVideoStore.getState().downloadAllRoundIds.includes(roundId),
    () => false,
  );
  useEffect(() => {
    if (!native) {
      return;
    }

    getDownloadEngine().syncWithCatalog(catalog.videos);
  }, [native, catalog.videos, roundDownloadEnabled]);

  return null;
}
